import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { SessionExpiredError } from '@traceio-org/auth-ui';
import type { AccessContext, MeAccess, ProductKey } from '@traceio-org/platform-contracts';
import { useHub } from './context';
import { isSessionEnded, sessionExpiredPath, useLoad, type LoadState } from './hooks';

/**
 * Loads me/access for the signed-in person. Without a session (or on a 401) it moves to the
 * session-expired page once; other failures stay on the page as a recoverable error.
 */
export function useMeAccess(productKey: ProductKey | null | undefined): [LoadState<MeAccess>, () => void] {
  const deps = useHub();
  const navigate = useNavigate();
  const [state, retry] = useLoad(
    async (signal) => {
      const session = await deps.sessions.restore();
      if (!session) throw new SessionExpiredError('no_session');
      return deps.client.meAccess({ signal });
    },
    [deps],
  );
  const ended = state.status === 'error' && isSessionEnded(state.error);
  useEffect(() => {
    if (ended) navigate(sessionExpiredPath(productKey), { replace: true });
  }, [ended, navigate, productKey]);
  return [state, retry];
}

/**
 * Like useMeAccess, but for pages that also work signed out (access denied, no workspace, unavailable):
 * no session simply means no personal details.
 */
export function useOptionalMeAccess(): LoadState<MeAccess | null> {
  const deps = useHub();
  const [state] = useLoad(async (signal) => {
    const session = await deps.sessions.restore();
    if (!session) return null;
    try {
      return await deps.client.meAccess({ signal });
    } catch (error) {
      if (isSessionEnded(error)) return null;
      throw error;
    }
  }, [deps]);
  return state;
}

/** Display name of the organization behind a context (the provider for a portal context). */
export function organizationNameOf(context: AccessContext | undefined): string | undefined {
  if (!context) return undefined;
  if (context.type === 'employee') return context.organization.displayName;
  if (context.type === 'portal') return context.providerOrganization.displayName;
  return 'Trace';
}
